import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Text } from "@nbs/ui";
import { usePermissions } from "@/hooks/usePermissions";

type BannerAction = {
  readonly key: string;
  readonly to?: string;
};

/**
 * Role-scoped context strip — surfaces concrete next actions per profile.
 * Complements GraphQL filtering (never a substitute for it).
 */
export function RoleContextBanner() {
  const { t } = useTranslation();
  const permissions = usePermissions();

  if (!permissions.can("ui.roleBanner")) return null;

  const extras: BannerAction[] = [];

  if (permissions.can("ui.adminExtras")) {
    extras.push({ key: "rbac.banner.reviewerQueue", to: "/review" });
    extras.push({
      key: "rbac.banner.managerPublishQueue",
      to: "/measures?statuses=validee",
    });
    extras.push({ key: "rbac.banner.adminSourceReport", to: "/source-report" });
    extras.push({ key: "rbac.banner.adminAccounts", to: "/settings" });
  } else if (permissions.can("ui.managerExtras")) {
    extras.push({
      key: "rbac.banner.managerPublishQueue",
      to: "/measures?statuses=validee",
    });
    extras.push({ key: "rbac.banner.managerPilot", to: "/dashboard" });
  } else if (permissions.can("ui.scientistExtras")) {
    extras.push({ key: "rbac.banner.scientistQueue", to: "/review" });
    extras.push({
      key: "rbac.banner.scientistValidation",
      to: "/measures?statuses=controlee",
    });
    extras.push({ key: "rbac.banner.scientistConfidence", to: "/measures?qualites=incertaine" });
  } else if (permissions.can("ui.reviewerExtras")) {
    extras.push({ key: "rbac.banner.reviewerQueue", to: "/review" });
    extras.push({
      key: "rbac.banner.reviewerIncomplete",
      to: "/measures?statuses=saisie,controlee",
    });
  } else {
    extras.push({ key: "rbac.banner.viewerPublishedOnly" });
    if (permissions.can("nav.n1")) {
      extras.push({ key: "rbac.banner.viewerExploreN1", to: "/n1" });
    }
  }

  return (
    <aside
      className="nbs-role-banner"
      data-testid="role-context-banner"
      data-role={permissions.productLabel}
      data-tour="role-banner"
      aria-label={t("rbac.banner.label")}
    >
      <Text>
        <strong>
          {t("rbac.banner.role", { role: t(`rbac.roles.${permissions.productLabel}`) })}
        </strong>
        {" — "}
        {t(`rbac.banner.scope.${permissions.productLabel}`)}
      </Text>
      <ul className="nbs-role-banner__extras">
        {extras.map((item) => (
          <li key={item.key}>{item.to ? <Link to={item.to}>{t(item.key)}</Link> : t(item.key)}</li>
        ))}
      </ul>
    </aside>
  );
}
